import { useState } from 'react';
import {
  Activity,
  AlertTriangle,
  Building2,
  CalendarCheck,
  CalendarClock,
  Check,
  CheckCircle2,
  ChevronRight,
  Clock,
  Flag,
  ListChecks,
  Pencil,
  ShieldAlert,
  Tag,
  Users,
  Wallet,
  type LucideIcon,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { updateProjectServices, type ProjectDetail } from '../../lib/projectsApi';
import type { OverviewMonthly, OverviewMilestone, ProjectOverview } from '../../lib/kbProjectsApi';
import { useProjectOverview } from '../../hooks/useKbProject';
import { useTaskStatuses } from '../../hooks/useTaskStatuses';
import { useServices } from '../../hooks/useServices';
import { useUsers } from '../../hooks/useUsers';
import { usePermissions } from '../../hooks/usePermissions';
import { HealthBadge, StatusBadge } from './ProjectBadges';
import { Skeleton, SkeletonText } from '../ui/Skeleton';
import { Popover } from '../tasks/linear/Popover';
import { Avatar } from '../dashboard/Avatar';
import { useI18n } from '../../contexts/I18nContext';

// --- Formatting helpers ----------------------------------------------------

function formatDate(value: string | null): string {
  return value ? new Date(value).toLocaleDateString('ro-RO') : '—';
}

function formatMoney(value: number | null | undefined, currency = 'RON'): string {
  return value == null
    ? '—'
    : value.toLocaleString('ro-RO', {
        style: 'currency',
        currency,
        maximumFractionDigits: 0,
      });
}

function formatStatusLabel(value: string): string {
  return value
    .toLowerCase()
    .replace(/_/g, ' ')
    .replace(/^\w/, (c) => c.toUpperCase());
}

// --- Layout primitives -----------------------------------------------------

function Section({
  title,
  icon: Icon,
  count,
  action,
  children,
}: {
  title: string;
  icon?: LucideIcon;
  count?: number;
  action?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm sm:p-5">
      <header className="mb-4 flex items-center justify-between gap-3">
        <h3 className="flex items-center gap-2 text-sm font-semibold text-gray-900">
          {Icon && (
            <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-brand-50 text-brand-600">
              <Icon size={15} />
            </span>
          )}
          {title}
          {count != null && (
            <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">
              {count}
            </span>
          )}
        </h3>
        {action}
      </header>
      {children}
    </section>
  );
}

/** A labelled fact with an icon, used in the header card. */
function Fact({
  icon: Icon,
  label,
  value,
  hint,
  hintTone = 'muted',
}: {
  icon: LucideIcon;
  label: string;
  value: React.ReactNode;
  hint?: string;
  hintTone?: 'muted' | 'negative' | 'warning';
}) {
  const hintClass =
    hintTone === 'negative'
      ? 'text-red-600'
      : hintTone === 'warning'
        ? 'text-amber-600'
        : 'text-gray-500';
  return (
    <div className="flex min-w-0 items-start gap-3">
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-gray-50 text-gray-500 ring-1 ring-gray-200">
        <Icon size={16} />
      </span>
      <div className="min-w-0">
        <dt className="text-xs text-gray-500">{label}</dt>
        <dd className="truncate text-sm font-semibold text-gray-900">{value}</dd>
        {hint && <p className={`text-xs ${hintClass}`}>{hint}</p>}
      </div>
    </div>
  );
}

/** Big number tile (tasks, counts). */
function CountTile({
  label,
  value,
  tone = 'default',
}: {
  label: string;
  value: number;
  tone?: 'default' | 'positive' | 'negative' | 'brand';
}) {
  const toneClass =
    tone === 'positive'
      ? 'bg-emerald-50 text-emerald-700'
      : tone === 'negative'
        ? 'bg-red-50 text-red-700'
        : tone === 'brand'
          ? 'bg-brand-50 text-brand-700'
          : 'bg-gray-50 text-gray-900';
  return (
    <div className={`rounded-lg px-3 py-2.5 ${toneClass}`}>
      <dd className="text-2xl font-semibold leading-tight">{value}</dd>
      <dt className="text-xs opacity-80">{label}</dt>
    </div>
  );
}

const DAY_MS = 24 * 60 * 60 * 1000;

function startOfToday(): Date {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
}

/** Whole days from today to `value` (negative when it is in the past). */
function daysUntil(value: string): number {
  const d = new Date(value);
  d.setHours(0, 0, 0, 0);
  return Math.round((d.getTime() - startOfToday().getTime()) / DAY_MS);
}

// --- C. Income vs outcome chart --------------------------------------------

/** 192868 → "193k"; keeps the y axis readable without a thousands separator. */
function compactMoney(value: number): string {
  const abs = Math.abs(value);
  if (abs >= 1_000_000) return `${(value / 1_000_000).toFixed(abs >= 10_000_000 ? 0 : 1)}M`;
  if (abs >= 1_000) return `${Math.round(value / 1_000)}k`;
  return String(Math.round(value));
}

/** "2026-06" → "iun. 26". Falls back to the raw string for anything unexpected. */
function monthLabel(month: string): string {
  const m = /^(\d{4})-(\d{2})$/.exec(month);
  if (!m) return month;
  const short = new Date(Number(m[1]), Number(m[2]) - 1, 1).toLocaleDateString('ro-RO', {
    month: 'short',
  });
  return `${short} ${m[1].slice(2)}`;
}

/**
 * Monthly revenue vs expenses. The month groups share the plot width evenly,
 * so a project with four months fills the card instead of huddling in the left
 * corner; past a dozen months the groups hit their minimum width and the plot
 * scrolls sideways rather than squeezing the bars into threads.
 */
function IncomeOutcomeChart({ monthly }: { monthly: OverviewMonthly[] }) {
  const { t } = useI18n();
  if (monthly.length === 0) {
    return <p className="text-sm text-gray-500">{t('projects.overview.noMonthly')}</p>;
  }
  const max = Math.max(1, ...monthly.map((m) => Math.max(m.income, m.outcome)));
  // A zero month stays flat on the axis; anything above it keeps a visible
  // sliver, so a small month does not disappear next to a large one.
  const barHeight = (value: number) => (value <= 0 ? 0 : Math.max(2, (value / max) * 100));
  const legend = [
    { color: 'bg-emerald-500', label: t('projects.overview.revenue') },
    { color: 'bg-red-400', label: t('projects.overview.expenses') },
  ];

  return (
    <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">
          {t('projects.overview.monthlyTrend')}
        </span>
        <div className="flex gap-4 text-xs text-gray-600">
          {legend.map((l) => (
            <span key={l.label} className="flex items-center gap-1.5">
              <span className={`inline-block h-2 w-2 rounded-sm ${l.color}`} />
              {l.label}
            </span>
          ))}
        </div>
      </div>

      <div className="flex gap-3">
        <div className="flex h-36 w-12 flex-col justify-between text-right text-[10px] text-gray-400">
          <span>{compactMoney(max)}</span>
          <span>{compactMoney(max / 2)}</span>
          <span>0</span>
        </div>
        <div className="min-w-0 flex-1 overflow-x-auto">
          <div className="min-w-full">
            <div className="relative h-36">
              {[0, 50, 100].map((pct) => (
                <div
                  key={pct}
                  className="absolute inset-x-0 border-t border-gray-200"
                  style={{ top: `${pct}%` }}
                />
              ))}
              <div className="relative flex h-full items-end gap-2">
                {monthly.map((m) => (
                  <div
                    key={m.month}
                    className="flex h-full min-w-[56px] flex-1 items-end justify-center gap-1.5"
                  >
                    <div
                      className="w-1/3 max-w-[40px] rounded-t bg-emerald-500"
                      style={{ height: `${barHeight(m.income)}%` }}
                      title={`${t('projects.overview.revenue')}: ${formatMoney(m.income)}`}
                    />
                    <div
                      className="w-1/3 max-w-[40px] rounded-t bg-red-400"
                      style={{ height: `${barHeight(m.outcome)}%` }}
                      title={`${t('projects.overview.expenses')}: ${formatMoney(m.outcome)}`}
                    />
                  </div>
                ))}
              </div>
            </div>
            {/* Same gap and flex basis as the bar groups, so labels stay under them. */}
            <div className="mt-2 flex gap-2">
              {monthly.map((m) => (
                <span
                  key={m.month}
                  className="min-w-[56px] flex-1 whitespace-nowrap text-center text-[10px] text-gray-500"
                >
                  {monthLabel(m.month)}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// --- D. Milestone timeline -------------------------------------------------

function MilestoneRow({ m }: { m: OverviewMilestone }) {
  const { t } = useI18n();
  const done = m.progress >= 100 || /done|complet|finaliz/i.test(m.status);
  const barColor = m.overdue ? 'bg-red-500' : done ? 'bg-emerald-500' : 'bg-brand-600';
  return (
    <div
      className={`rounded-md border p-3 ${m.overdue ? 'border-red-200 bg-red-50' : 'border-gray-200'}`}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm font-medium text-gray-900">{m.name}</span>
        <span className="flex items-center gap-2 text-xs text-gray-500">
          {m.overdue && (
            <span className="inline-flex items-center gap-1 font-medium text-red-600">
              <AlertTriangle size={12} /> {t('projects.overview.overdue')}
            </span>
          )}
          {formatDate(m.deadline)}
        </span>
      </div>
      <div className="mt-2 flex items-center gap-2">
        <div className="h-2 flex-1 overflow-hidden rounded-full bg-gray-100">
          <div
            className={`h-full ${barColor}`}
            style={{ width: `${Math.min(100, Math.max(0, m.progress))}%` }}
          />
        </div>
        <span className="w-10 text-right text-xs text-gray-600">{m.progress}%</span>
      </div>
    </div>
  );
}

// --- E. Task status breakdown ---------------------------------------------

// byStatus is keyed by status slug (configurable statuses); order, labels and
// colors come from the live status definitions. One stacked bar + a legend
// reads faster than a bar per status.
function TaskStatusBreakdown({ byStatus }: { byStatus: Record<string, number> }) {
  const { bySlug } = useTaskStatuses();
  const entries = Object.entries(byStatus)
    .filter(([, n]) => n > 0)
    .sort((a, b) => (bySlug[a[0]]?.order ?? 999) - (bySlug[b[0]]?.order ?? 999));
  const total = entries.reduce((sum, [, n]) => sum + n, 0);
  if (total === 0) return null;
  const colorOf = (slug: string) => bySlug[slug]?.color ?? '#9ca3af';
  return (
    <div>
      <div className="flex h-2.5 overflow-hidden rounded-full bg-gray-100">
        {entries.map(([slug, n]) => (
          <div
            key={slug}
            className="h-full first:rounded-l-full last:rounded-r-full"
            style={{ width: `${(n / total) * 100}%`, backgroundColor: colorOf(slug) }}
            title={`${bySlug[slug]?.name ?? formatStatusLabel(slug)}: ${n}`}
          />
        ))}
      </div>
      <ul className="mt-3 flex flex-wrap gap-x-4 gap-y-1.5">
        {entries.map(([slug, n]) => (
          <li key={slug} className="flex items-center gap-1.5 text-xs text-gray-600">
            <span
              className="inline-block h-2 w-2 rounded-full"
              style={{ backgroundColor: colorOf(slug) }}
            />
            {bySlug[slug]?.name ?? formatStatusLabel(slug)}
            <span className="font-semibold text-gray-900">{n}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

// --- Sections --------------------------------------------------------------

function GeneralSection({ data, project }: { data: ProjectOverview; project: ProjectDetail }) {
  const { t } = useI18n();
  const p = data.project;
  // CLE-432: financials are omitted for roles without projects.canViewFinancials.
  const consumed = data.financials?.totalCost ?? null;
  const budgetUsed = consumed !== null && p.budget ? Math.round((consumed / p.budget) * 100) : null;

  // Elapsed share of the start → deadline window, so the card says at a
  // glance how far into the schedule the project is.
  let timeline: { pct: number; daysLeft: number } | null = null;
  if (p.startDate && p.endDate) {
    const start = new Date(p.startDate).getTime();
    const end = new Date(p.endDate).getTime();
    if (end > start) {
      const pct = ((Date.now() - start) / (end - start)) * 100;
      timeline = { pct: Math.min(100, Math.max(0, pct)), daysLeft: daysUntil(p.endDate) };
    }
  }
  const deadlineHint =
    p.endDate == null
      ? undefined
      : timeline && timeline.daysLeft < 0
        ? t('projects.overview.daysOverdue', { n: -timeline.daysLeft })
        : t('projects.overview.daysLeft', { n: daysUntil(p.endDate) });
  const deadlineTone =
    p.endDate && daysUntil(p.endDate) < 0
      ? 'negative'
      : p.endDate && daysUntil(p.endDate) <= 14
        ? 'warning'
        : 'muted';

  return (
    <section className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm sm:p-5">
      <div className="flex flex-wrap items-center gap-2">
        <StatusBadge status={p.status} />
        <HealthBadge health={p.health} />
        {p.isBarter && (
          <span className="inline-block rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700">
            {t('projects.overview.barter')}
          </span>
        )}
      </div>
      {p.description && (
        <p className="mt-3 max-w-3xl text-sm leading-relaxed text-gray-600">{p.description}</p>
      )}

      <dl className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Fact
          icon={Building2}
          label={t('projects.overview.client')}
          value={p.client ?? project.owner?.name ?? '—'}
        />
        <Fact
          icon={CalendarCheck}
          label={t('projects.overview.start')}
          value={formatDate(p.startDate)}
        />
        <Fact
          icon={CalendarClock}
          label={t('projects.overview.deadline')}
          value={formatDate(p.endDate)}
          hint={deadlineHint}
          hintTone={deadlineTone}
        />
        <Fact
          icon={Wallet}
          label={t('projects.overview.budget')}
          value={formatMoney(p.budget, p.budgetCurrency)}
          hint={
            consumed !== null
              ? `${t('projects.overview.consumed')} ${formatMoney(consumed)}${budgetUsed !== null ? ` · ${budgetUsed}%` : ''}`
              : undefined
          }
          hintTone={budgetUsed !== null && budgetUsed > 100 ? 'negative' : 'muted'}
        />
      </dl>

      {timeline && (
        <div className="mt-5">
          <div className="mb-1.5 flex items-center justify-between text-xs text-gray-500">
            <span>{t('projects.overview.timeline')}</span>
            <span className="font-medium text-gray-700">{Math.round(timeline.pct)}%</span>
          </div>
          <div className="h-2 overflow-hidden rounded-full bg-gray-100">
            <div
              className={`h-full rounded-full ${timeline.daysLeft < 0 ? 'bg-red-500' : 'bg-brand-600'}`}
              style={{ width: `${timeline.pct}%` }}
            />
          </div>
        </div>
      )}
    </section>
  );
}

// Services nomenclature: the project's attached services (DB-only metadata,
// PATCH /api/projects/:id/services). Chips + a multi-select popover; editing is
// gated on projects.canEdit. The KB markdown does not carry these links, so
// they are edited here rather than in the (KB-backed) Edit Project modal.
function ServicesSection({ project }: { project: ProjectDetail }) {
  const { t } = useI18n();
  const { can } = usePermissions();
  const canEdit = can('projects', 'canEdit');
  const { data: services } = useServices();
  const qc = useQueryClient();
  const [pending, setPending] = useState(false);
  const [error, setError] = useState(false);

  const selectedIds = new Set(project.services.map((s) => s.id));

  async function toggleService(serviceId: string) {
    if (pending) return;
    const next = new Set(selectedIds);
    if (next.has(serviceId)) next.delete(serviceId);
    else next.add(serviceId);
    setError(false);
    setPending(true);
    try {
      await updateProjectServices(project.id, [...next]);
      await qc.invalidateQueries({ queryKey: ['project', project.id] });
    } catch {
      setError(true);
    } finally {
      setPending(false);
    }
  }

  return (
    <Section title={t('projects.services.title')} icon={Tag}>
      <div className="flex flex-wrap items-center gap-1.5">
        {project.services.length === 0 && (
          <p className="text-sm text-gray-500">{t('projects.services.empty')}</p>
        )}
        {project.services.map((s) => (
          <span
            key={s.id}
            className="inline-flex items-center gap-1.5 rounded-full border border-gray-200 px-2.5 py-0.5 text-xs font-medium text-gray-700"
          >
            {s.color && (
              <span className="h-2 w-2 rounded-full" style={{ backgroundColor: s.color }} />
            )}
            {s.name}
          </span>
        ))}
        {canEdit && (
          <Popover
            width={260}
            trigger={({ toggle, ref }) => (
              <button
                type="button"
                ref={(el) => ref(el)}
                onClick={toggle}
                disabled={pending}
                className="inline-flex items-center gap-1 rounded-full border border-dashed border-gray-300 px-2.5 py-0.5 text-xs text-gray-500 hover:border-gray-400 hover:text-gray-700 disabled:opacity-50"
              >
                <Pencil size={11} />
                {t('projects.services.edit')}
              </button>
            )}
          >
            {() => (
              <div className="max-h-64 overflow-y-auto py-1">
                {(services ?? []).length === 0 && (
                  <p className="px-3 py-2 text-sm text-gray-400">{t('projects.services.none')}</p>
                )}
                {(services ?? []).map((s) => {
                  const isOn = selectedIds.has(s.id);
                  return (
                    <button
                      key={s.id}
                      type="button"
                      onClick={() => void toggleService(s.id)}
                      className="ui-menu-item justify-between"
                    >
                      <span className="flex min-w-0 items-center gap-2">
                        {s.color && (
                          <span
                            className="inline-block h-2.5 w-2.5 shrink-0 rounded-full"
                            style={{ backgroundColor: s.color }}
                          />
                        )}
                        <span className="truncate">{s.name}</span>
                      </span>
                      {isOn && <Check size={14} className="shrink-0 text-gray-400" />}
                    </button>
                  );
                })}
              </div>
            )}
          </Popover>
        )}
      </div>
      {error && <p className="mt-2 text-sm text-red-600">{t('projects.services.error')}</p>}
    </Section>
  );
}

// The team comes from the project's allocations (the same rows the Team tab
// shows), not from the KB README's `## Echipa` table: that table can hold
// stale rows (old emails, legacy percentages like "100%") and only has emails,
// so the two tabs disagreed on who is on the project and for how many hours.
function TeamSection({ team }: { team: ProjectDetail['team'] }) {
  const { t } = useI18n();
  const members = [...team.members].sort(
    (a, b) => b.allocationHours - a.allocationHours || a.fullName.localeCompare(b.fullName),
  );
  const totalHours = members.reduce((sum, m) => sum + m.allocationHours, 0);
  const maxHours = Math.max(1, ...members.map((m) => m.allocationHours));
  return (
    <Section
      title={t('projects.overview.team')}
      icon={Users}
      count={members.length}
      action={
        totalHours > 0 ? (
          <span className="text-xs text-gray-500">
            <span className="font-semibold text-gray-900">{totalHours}</span>
            {t('projects.overview.allocPerWeek')} {t('projects.overview.inTotal')}
          </span>
        ) : undefined
      }
    >
      {members.length === 0 ? (
        <p className="text-sm text-gray-500">{t('projects.overview.noTeam')}</p>
      ) : (
        <ul className="space-y-3">
          {members.map((m) => (
            <li key={m.id} className="flex items-center gap-3">
              <Avatar name={m.fullName} id={m.id} src={m.avatarUrl} size={34} />
              <div className="min-w-0 flex-1">
                <div className="flex items-baseline justify-between gap-2">
                  <span className="truncate text-sm font-medium text-gray-900">{m.fullName}</span>
                  <span
                    className={`shrink-0 text-xs font-medium ${m.allocationHours > 0 ? 'text-gray-700' : 'text-gray-400'}`}
                  >
                    {m.allocationHours > 0
                      ? `${m.allocationHours}${t('projects.overview.allocPerWeek')}`
                      : t('projects.team.inactive')}
                  </span>
                </div>
                <div className="mt-1 flex items-center gap-2">
                  <span className="min-w-0 flex-1 truncate text-xs text-gray-500">
                    {m.title ?? m.email}
                  </span>
                  <div className="h-1.5 w-20 shrink-0 overflow-hidden rounded-full bg-gray-100 sm:w-28">
                    <div
                      className="h-full rounded-full bg-brand-500"
                      style={{ width: `${(m.allocationHours / maxHours) * 100}%` }}
                    />
                  </div>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </Section>
  );
}

/** One figure of the project P&L, as a tile inside the summary card. */
function PnlTile({
  label,
  value,
  hint,
  tone = 'default',
}: {
  label: string;
  value: string;
  hint?: string;
  tone?: 'default' | 'positive' | 'negative';
}) {
  const valueClass =
    tone === 'positive'
      ? 'text-emerald-600'
      : tone === 'negative'
        ? 'text-red-600'
        : 'text-gray-900';
  return (
    <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
      <dt className="text-xs uppercase tracking-wide text-gray-500">{label}</dt>
      <dd className={`mt-1 text-xl font-semibold ${valueClass}`}>{value}</dd>
      {hint && <p className="mt-0.5 text-xs text-gray-500">{hint}</p>}
    </div>
  );
}

function FinancialsSection({
  financials,
}: {
  financials: NonNullable<ProjectOverview['financials']>;
}) {
  const { t } = useI18n();
  const profitTone = financials.profit >= 0 ? 'positive' : 'negative';
  const margin =
    financials.totalRevenue > 0
      ? `${t('projects.overview.margin')} ${((financials.profit / financials.totalRevenue) * 100).toFixed(1)}%`
      : undefined;
  return (
    <Section title={t('projects.overview.financialSummary')} icon={Wallet}>
      <dl className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-5">
        <PnlTile
          label={t('projects.overview.revenue')}
          value={formatMoney(financials.totalRevenue)}
          tone="positive"
        />
        <PnlTile
          label={t('projects.overview.expenses')}
          value={formatMoney(financials.totalCost)}
          tone="negative"
        />
        <PnlTile
          label={
            financials.profit >= 0 ? t('projects.overview.profit') : t('projects.overview.loss')
          }
          value={formatMoney(financials.profit)}
          hint={margin}
          tone={profitTone}
        />
        <PnlTile
          label={t('projects.overview.unpaidIn')}
          value={formatMoney(financials.unpaidIncome)}
        />
        <PnlTile
          label={t('projects.overview.payableOut')}
          value={formatMoney(financials.payableOutcome)}
        />
      </dl>
      <div className="mt-4">
        <IncomeOutcomeChart monthly={financials.monthly} />
      </div>
    </Section>
  );
}

function MilestonesSection({ milestones }: { milestones: ProjectOverview['milestones'] }) {
  const { t } = useI18n();
  return (
    <Section title={t('projects.overview.milestones')} icon={Flag}>
      <div className="mb-3 flex items-center gap-3">
        <div className="h-2 flex-1 overflow-hidden rounded-full bg-gray-100">
          <div className="h-full bg-brand-600" style={{ width: `${milestones.completion}%` }} />
        </div>
        <span className="text-sm font-semibold text-gray-700">{milestones.completion}%</span>
      </div>
      {milestones.items.length === 0 ? (
        <p className="text-sm text-gray-500">{t('projects.overview.noMilestones')}</p>
      ) : (
        <div className="space-y-2">
          {milestones.items.map((m) => (
            <MilestoneRow key={m.name} m={m} />
          ))}
        </div>
      )}
    </Section>
  );
}

const UPCOMING_LIMIT = 5;

function TasksSection({
  tasks,
  projectTasks,
}: {
  tasks: ProjectOverview['tasks'];
  projectTasks: ProjectDetail['tasks'];
}) {
  const { t } = useI18n();
  const { bySlug } = useTaskStatuses();
  const done = Object.entries(tasks.byStatus).reduce(
    (sum, [slug, n]) => (bySlug[slug]?.category === 'COMPLETED' ? sum + n : sum),
    0,
  );
  const completion = tasks.total > 0 ? Math.round((done / tasks.total) * 100) : 0;

  // Open tasks, the most pressing first: overdue, then by due date, then the
  // ones without a date.
  const upcoming = projectTasks
    .filter((task) => task.statusCategory !== 'COMPLETED' && task.statusCategory !== 'CANCELLED')
    .sort((a, b) => {
      if (!a.dueDate) return b.dueDate ? 1 : 0;
      if (!b.dueDate) return -1;
      return new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime();
    })
    .slice(0, UPCOMING_LIMIT);
  const { data: users } = useUsers();
  const nameOf = (userId: string | null) =>
    userId ? (users?.find((u) => u.id === userId)?.name ?? null) : null;

  return (
    <Section
      title={t('projects.overview.tasks')}
      icon={ListChecks}
      count={tasks.total}
      action={
        <Link
          to="?tab=tasks"
          className="inline-flex items-center gap-0.5 text-xs font-medium text-brand-700 hover:text-brand-800"
        >
          {t('projects.overview.seeAllTasks')}
          <ChevronRight size={14} />
        </Link>
      }
    >
      {tasks.total === 0 ? (
        <p className="text-sm text-gray-500">{t('projects.overview.noTasks')}</p>
      ) : (
        <>
          <div className="flex items-center gap-4">
            <CompletionRing pct={completion} />
            <dl className="grid flex-1 grid-cols-3 gap-2">
              <CountTile label={t('projects.overview.active')} value={tasks.active} tone="brand" />
              <CountTile
                label={t('projects.overview.overdueTasks')}
                value={tasks.overdue}
                tone={tasks.overdue > 0 ? 'negative' : 'default'}
              />
              <CountTile label={t('projects.overview.doneTasks')} value={done} tone="positive" />
            </dl>
          </div>

          <div className="mt-4">
            <TaskStatusBreakdown byStatus={tasks.byStatus} />
          </div>

          <div className="mt-5">
            <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
              {t('projects.overview.nextTasks')}
            </h4>
            {upcoming.length === 0 ? (
              <p className="flex items-center gap-2 text-sm text-gray-500">
                <CheckCircle2 size={15} className="text-emerald-500" />
                {t('projects.overview.allDone')}
              </p>
            ) : (
              <ul className="divide-y divide-gray-100 rounded-lg border border-gray-200">
                {upcoming.map((task) => (
                  <UpcomingTaskRow key={task.id} task={task} assignee={nameOf(task.assigneeId)} />
                ))}
              </ul>
            )}
          </div>
        </>
      )}
    </Section>
  );
}

function UpcomingTaskRow({
  task,
  assignee,
}: {
  task: ProjectDetail['tasks'][number];
  assignee: string | null;
}) {
  const { t } = useI18n();
  const days = task.dueDate ? daysUntil(task.dueDate) : null;
  const dueClass =
    days === null
      ? 'text-gray-400'
      : days < 0
        ? 'font-medium text-red-600'
        : days <= 2
          ? 'font-medium text-amber-600'
          : 'text-gray-500';
  const dueLabel =
    days === null
      ? t('projects.overview.noDueDate')
      : days === 0
        ? t('projects.overview.dueToday')
        : formatDate(task.dueDate);
  return (
    <li>
      <Link
        to={`/tasks/${encodeURIComponent(task.id)}`}
        className="flex items-center gap-3 px-3 py-2 text-sm transition-colors hover:bg-gray-50"
      >
        <span
          className="inline-block h-2.5 w-2.5 shrink-0 rounded-full"
          style={{ backgroundColor: task.statusColor ?? '#9ca3af' }}
          title={task.statusLabel ?? task.status}
        />
        <span className="min-w-0 flex-1 truncate text-gray-800">{task.title}</span>
        <span className={`flex shrink-0 items-center gap-1 text-xs ${dueClass}`}>
          {days !== null && days < 0 && <AlertTriangle size={12} />}
          {dueLabel}
        </span>
        {task.assigneeId && (
          <span title={assignee ?? undefined}>
            <Avatar
              name={assignee ?? '?'}
              id={task.assigneeId}
              userId={task.assigneeId}
              size={22}
            />
          </span>
        )}
      </Link>
    </li>
  );
}

/** Donut showing the share of completed tasks. */
function CompletionRing({ pct }: { pct: number }) {
  const { t } = useI18n();
  const r = 26;
  const c = 2 * Math.PI * r;
  return (
    <div className="relative h-16 w-16 shrink-0" title={t('projects.overview.completion')}>
      <svg viewBox="0 0 64 64" className="h-16 w-16 -rotate-90">
        <circle cx="32" cy="32" r={r} fill="none" strokeWidth="7" className="stroke-gray-100" />
        <circle
          cx="32"
          cy="32"
          r={r}
          fill="none"
          strokeWidth="7"
          strokeLinecap="round"
          className="stroke-brand-600"
          strokeDasharray={c}
          strokeDashoffset={c - (pct / 100) * c}
        />
      </svg>
      <span className="absolute inset-0 flex items-center justify-center text-sm font-semibold text-gray-900">
        {pct}%
      </span>
    </div>
  );
}

function RisksSection({ risks }: { risks: ProjectOverview['risks'] }) {
  const { t } = useI18n();
  return (
    <Section title={t('projects.overview.risks')} icon={ShieldAlert} count={risks.length}>
      {risks.length === 0 ? (
        <p className="text-sm text-gray-500">{t('projects.overview.noRisks')}</p>
      ) : (
        <ul className="space-y-2">
          {risks.map((r) => (
            <li
              key={r.name}
              className={`rounded-md border p-3 ${r.critical ? 'border-red-200 bg-red-50' : 'border-gray-200'}`}
            >
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm font-medium text-gray-900">{r.name}</span>
                {r.critical && (
                  <span className="inline-flex items-center gap-1 text-xs font-medium text-red-600">
                    <AlertTriangle size={12} /> {t('projects.overview.critical')}
                  </span>
                )}
              </div>
              <p className="mt-1 text-xs text-gray-600">
                <span className="font-medium">{t('projects.overview.impact')}</span>{' '}
                {r.impact || '—'} ·{' '}
                <span className="font-medium">{t('projects.overview.action')}</span>{' '}
                {r.action || '—'}
              </p>
            </li>
          ))}
        </ul>
      )}
    </Section>
  );
}

function RecentLogSection({ entries }: { entries: ProjectOverview['recentLog'] }) {
  const { t } = useI18n();
  return (
    <Section title={t('projects.overview.recentActivity')} icon={Activity}>
      {entries.length === 0 ? (
        <p className="text-sm text-gray-500">{t('projects.overview.noActivity')}</p>
      ) : (
        <ul className="space-y-2">
          {entries.map((e, i) => (
            <li key={`${e.timestamp}-${i}`} className="flex gap-3 text-sm">
              <span className="flex shrink-0 items-center gap-1 text-xs text-gray-400">
                <Clock size={12} />
                {e.timestamp ? new Date(e.timestamp).toLocaleDateString('ro-RO') : '—'}
              </span>
              <span className="text-gray-700">{e.message}</span>
            </li>
          ))}
        </ul>
      )}
    </Section>
  );
}

// --- Tab -------------------------------------------------------------------

export function ProjectOverviewTab({ project }: { project: ProjectDetail }) {
  const { t } = useI18n();
  const { can } = usePermissions();
  const codeOrId = project.code ?? project.id;
  const { data, isLoading, isError } = useProjectOverview(codeOrId);

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="rounded-lg border border-gray-200 bg-white p-5">
          <Skeleton className="h-4" width={120} />
          <SkeletonText className="mt-4" lines={3} />
        </div>
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          <div className="rounded-lg border border-gray-200 bg-white p-5">
            <SkeletonText lines={4} />
          </div>
          <div className="rounded-lg border border-gray-200 bg-white p-5">
            <SkeletonText lines={4} />
          </div>
        </div>
      </div>
    );
  }

  if (isError || !data) {
    return <p className="text-sm text-red-600">{t('projects.overview.error')}</p>;
  }

  return (
    <div className="space-y-6">
      <GeneralSection data={data} project={project} />
      <ServicesSection project={project} />
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2 lg:items-start">
        <TasksSection tasks={data.tasks} projectTasks={project.tasks} />
        <TeamSection team={project.team} />
      </div>
      {can('projects', 'canViewFinancials') && data.financials && (
        <FinancialsSection financials={data.financials} />
      )}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <MilestonesSection milestones={data.milestones} />
        <RisksSection risks={data.risks} />
      </div>
      <RecentLogSection entries={data.recentLog} />
    </div>
  );
}
